import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  ZoomIn as ZoomInIcon,
  Close as CloseIcon,
  Draw as DrawIcon,
  Image as ImageIcon,
  Check as CheckIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { makeApiCall } from '../../config/api';
import SiteDiagramDrawer from './SiteDiagramDrawer';

/**
 * Site Diagram Upload Component
 * 
 * Allows uploading a property layout/perimeter image for quotes
 * Matches legacy system's "Schedule C - Site Diagram" functionality
 * 
 * @param {Object} props
 * @param {number} props.quoteId - The quote ID
 * @param {boolean} props.readOnly - Whether the component is read-only
 */
const SiteDiagramUpload = ({ quoteId, readOnly = false, address = '', onValuesCalculated }) => {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [siteDiagram, setSiteDiagram] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mode, setMode] = useState('upload'); // 'upload' or 'draw'

  // Debug drawerOpen state changes
  useEffect(() => {
    console.log('SiteDiagramUpload - drawerOpen changed to:', drawerOpen);
  }, [drawerOpen]);

  // Reset state when quoteId changes
  useEffect(() => {
    setError(null);
    setSuccess(null);
    setSiteDiagram(null);
    setLoading(false);
    setUploading(false);
    setSelectedFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  }, [quoteId]);

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // Load existing site diagram on mount
  useEffect(() => {
    if (quoteId) {
      // Clear any previous state
      setError(null);
      setSuccess(null);
      setSiteDiagram(null);
      loadSiteDiagram();
    }
  }, [quoteId]);

  const loadSiteDiagram = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      console.log('🔍 [SITE DIAGRAM] Loading site diagram for quote:', quoteId);
      const response = await makeApiCall(`/api/files/site-diagram/${quoteId}`);
      
      console.log('🔍 [SITE DIAGRAM] Response status:', response.status);
      console.log('🔍 [SITE DIAGRAM] Response headers:', Object.fromEntries(response.headers.entries()));
      
      if (response.ok) {
        const data = await response.json();
        console.log('🔍 [SITE DIAGRAM] Response data:', data);
        setSiteDiagram(data);
      } else if (response.status === 404) {
        // No site diagram exists - this is normal, not an error
        console.log('🔍 [SITE DIAGRAM] No site diagram found for quote', quoteId, '- this is normal');
        setError(null);
        setSiteDiagram(null);
      } else {
        // Actual error occurred
        const errorText = await response.text();
        console.error('🔍 [SITE DIAGRAM] Error loading site diagram, status:', response.status, 'body:', errorText);
        setError('Failed to load site diagram');
      }
    } catch (err) {
      // Network or other error
      console.error('🔍 [SITE DIAGRAM] Error loading site diagram:', err);
      setError('Failed to load site diagram');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    console.log('File selected:', file);
    
    if (!file) {
      console.log('No file selected');
      setSelectedFile(null);
      setPreviewUrl(null);
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file (PNG, JPG, GIF, etc.)');
      setSelectedFile(null);
      setPreviewUrl(null);
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      setSelectedFile(null);
      setPreviewUrl(null);
      return;
    }

    // Create preview URL
    const url = URL.createObjectURL(file);
    setSelectedFile(file);
    setPreviewUrl(url);
    setError(null);

    console.log('File validation passed, starting upload...');
    await uploadFile(file);
  };

  const uploadFile = async (file) => {
    try {
      setUploading(true);
      setError(null);
      setSuccess(null);

      console.log('📤 [SITE DIAGRAM] File validation passed, starting upload...');
      console.log('📤 [SITE DIAGRAM] File details:', {
        name: file.name,
        size: file.size,
        type: file.type
      });
      
      console.log('📤 [SITE DIAGRAM] Creating FormData for upload...');
      const formData = new FormData();
      formData.append('site_diagram', file);
      formData.append('quote_id', quoteId);

      console.log('📤 [SITE DIAGRAM] Sending upload request to /api/files/upload/site-diagram...');
      const response = await makeApiCall('/api/files/upload/site-diagram', {
        method: 'POST',
        body: formData
        // Note: Don't set Content-Type header - makeApiCall will detect FormData and let browser set it
      });

      console.log('📤 [SITE DIAGRAM] Upload response status:', response.status);
      console.log('📤 [SITE DIAGRAM] Upload response headers:', Object.fromEntries(response.headers.entries()));
      
      const data = await response.json();
      console.log('📤 [SITE DIAGRAM] Upload response data:', data);
      
      if (response.ok) {
        setSiteDiagram(data);
        setSuccess('Site diagram uploaded successfully!');
        // Clear success message after 5 seconds
        setTimeout(() => setSuccess(null), 5000);
      } else {
        throw new Error(data.error || 'Upload failed');
      }

    } catch (err) {
      console.error('📤 [SITE DIAGRAM] Upload error:', err);
      let errorMessage = 'Failed to upload site diagram';
      
      if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this site diagram?')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await makeApiCall(`/api/files/site-diagram/${quoteId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        setSiteDiagram(null);
        setSuccess('Site diagram deleted successfully!');
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to delete site diagram');
      }

    } catch (err) {
      console.error('Delete error:', err);
      setError(err.message || 'Failed to delete site diagram');
    } finally {
      setLoading(false);
    }
  };

  const openPreview = () => {
    setPreviewOpen(true);
  };

  const closePreview = () => {
    setPreviewOpen(false);
  };

  const handleDrawerSave = (data) => {
    setSiteDiagram(data);
    setDrawerOpen(false);
    setSuccess('Site diagram created successfully!');
    setTimeout(() => setSuccess(null), 5000);
  };

  const handleValuesCalculated = (values) => {
    if (onValuesCalculated) {
      onValuesCalculated(values);
    }
  };

  const applyCalculatedValues = () => {
    if (siteDiagram && siteDiagram.area_data) {
      const areaData = JSON.parse(siteDiagram.area_data);
      handleValuesCalculated(areaData);
      setSuccess('Calculated values applied to quote form!');
      setTimeout(() => setSuccess(null), 5000);
    }
  };

  // Check if address is complete
  const isAddressComplete = () => {
    return address && address.trim().length > 0;
  };

  if (loading && !siteDiagram) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Site Diagram
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Upload a property layout or draw areas on satellite imagery to calculate square footage
      </Typography>

      {!readOnly && (
        <Box sx={{ mb: 2 }}>
          <ToggleButtonGroup
            value={mode}
            exclusive
            onChange={(e, newMode) => {
              // Only allow switching to 'draw' if address is complete
              if (newMode === 'draw' && !isAddressComplete()) {
                setError('Please select a customer with a complete address to draw areas');
                return;
              }
              if (newMode) setMode(newMode);
            }}
            size="small"
          >
            <ToggleButton value="upload">
              <ImageIcon sx={{ mr: 1 }} />
              Upload Image
            </ToggleButton>
            <ToggleButton value="draw" disabled={!isAddressComplete()}>
              <DrawIcon sx={{ mr: 1 }} />
              Draw Areas
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {siteDiagram ? (
        // Display existing site diagram
        <Paper elevation={2} sx={{ p: 2, mt: 2 }}>
          <Box display="flex" alignItems="center" gap={2}>
            {/* Thumbnail */}
            <Box
              component="img"
              src={siteDiagram.signed_url}
              alt="Site Diagram"
              sx={{
                width: 80,
                height: 80,
                objectFit: 'cover',
                cursor: 'pointer',
                border: '2px solid #ddd',
                borderRadius: 1
              }}
              onClick={openPreview}
            />

            {/* File info */}
            <Box flex={1}>
              <Typography variant="body1" fontWeight="bold">
                {siteDiagram.filename}
              </Typography>
              <Typography variant="caption" color="textSecondary" display="block">
                Uploaded by {siteDiagram.uploaded_by} on{' '}
                {new Date(siteDiagram.uploaded_at).toLocaleDateString()}
              </Typography>
            </Box>

            {/* Actions */}
            {!readOnly && (
              <Box>
                <IconButton
                  color="primary"
                  onClick={openPreview}
                  title="View full size"
                >
                  <ZoomInIcon />
                </IconButton>
                <IconButton
                  color="error"
                  onClick={handleDelete}
                  disabled={loading}
                  title="Delete site diagram"
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            )}
          </Box>

          {!readOnly && (
            <Box mt={2} display="flex" gap={1} flexWrap="wrap">
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id={`site-diagram-replace-${quoteId}`}
                type="file"
                onChange={handleFileSelect}
                disabled={uploading}
              />
              <label htmlFor={`site-diagram-replace-${quoteId}`}>
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<UploadIcon />}
                  disabled={uploading}
                  size="small"
                >
                  Replace Diagram
                </Button>
              </label>
              
              {/* Edit drawn diagram button */}
              {siteDiagram && siteDiagram.is_drawn && (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    if (!isAddressComplete()) {
                      setError('Please select a customer with a complete address to edit the diagram');
                      return;
                    }
                    setDrawerOpen(true);
                  }}
                  size="small"
                  startIcon={<EditIcon />}
                  disabled={!isAddressComplete()}
                >
                  Edit Diagram
                </Button>
              )}
              
              {/* Apply calculated values button */}
              {siteDiagram && siteDiagram.area_data && (
                <Button
                  variant="contained"
                  color="success"
                  onClick={applyCalculatedValues}
                  size="small"
                  startIcon={<CheckIcon />}
                >
                  Apply to Quote
                </Button>
              )}
            </Box>
          )}
        </Paper>
      ) : (
        // Upload/Draw interface
        !readOnly && (
          <Paper elevation={2} sx={{ p: 3, mt: 2, textAlign: 'center' }}>
            {mode === 'upload' ? (
              <>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id={`site-diagram-upload-${quoteId}`}
                  type="file"
                  onChange={handleFileSelect}
                  disabled={uploading}
                />
                
                {/* File preview */}
                {previewUrl && selectedFile && (
                  <Box mb={2}>
                    <Typography variant="subtitle2" gutterBottom>
                      Preview: {selectedFile.name}
                    </Typography>
                    <Box
                      component="img"
                      src={previewUrl}
                      alt="Preview"
                      sx={{
                        maxWidth: '100%',
                        maxHeight: 200,
                        objectFit: 'contain',
                        border: '1px solid #ddd',
                        borderRadius: 1
                      }}
                    />
                  </Box>
                )}
                
                <label htmlFor={`site-diagram-upload-${quoteId}`}>
                  <Button
                    variant="contained"
                    component="span"
                    startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
                    disabled={uploading}
                  >
                    {uploading ? 'Uploading...' : 'Upload Site Diagram'}
                  </Button>
                </label>
                <Typography variant="caption" display="block" mt={1} color="textSecondary">
                  Supported formats: PNG, JPG, GIF (Max 10MB)
                </Typography>
              </>
            ) : (
              <>
                {isAddressComplete() ? (
                  <>
                    <Typography variant="body1" gutterBottom>
                      Draw areas on satellite imagery to automatically calculate square footage
                    </Typography>
                    {console.log('Rendering Draw Areas button, mode:', mode, 'drawerOpen:', drawerOpen)}
                    <Button
                      variant="contained"
                      onClick={() => {
                        console.log('Draw Areas button clicked, setting drawerOpen to true');
                        console.log('Current drawerOpen state before click:', drawerOpen);
                        setDrawerOpen(true);
                        console.log('setDrawerOpen(true) called');
                      }}
                      startIcon={<DrawIcon />}
                      sx={{ mt: 2 }}
                    >
                      Open Drawing Tool
                    </Button>
                    <Typography variant="caption" display="block" mt={1} color="textSecondary">
                      Requires address for accurate location
                    </Typography>
                  </>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <Typography variant="body1" color="textSecondary">
                      No Address Selected
                    </Typography>
                    <Typography variant="caption" display="block" mt={1} color="textSecondary">
                      Please select a customer with a complete address to draw areas
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </Paper>
        )
      )}

      {/* Full-size preview dialog */}
      {siteDiagram && (
        <Dialog
          open={previewOpen}
          onClose={closePreview}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            Site Diagram
            <IconButton
              aria-label="close"
              onClick={closePreview}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Box
              component="img"
              src={siteDiagram.signed_url}
              alt="Site Diagram Full Size"
              sx={{
                width: '100%',
                height: 'auto',
                maxHeight: '70vh',
                objectFit: 'contain'
              }}
            />
            <Typography variant="caption" display="block" mt={2} align="center" color="textSecondary">
              {siteDiagram.filename}
            </Typography>
          </DialogContent>
        </Dialog>
      )}

      {/* Site Diagram Drawer */}
      <SiteDiagramDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSave={handleDrawerSave}
        address={address}
        quoteId={quoteId}
        onValuesCalculated={handleValuesCalculated}
        existingAreaData={siteDiagram?.area_data}
      />
    </Box>
  );
};

export default SiteDiagramUpload;



